import * as React from "react";
import {Alert, Button, CircularProgress, Grid, TextField} from "@mui/material";
import FrontStepsLogo from "../assets/images/frontsteps-logo.png";
import {redirectToSuiteManagerLogin, useAuth0} from "../Auth";
import {styled} from "@mui/material/styles";
import QueryString from "query-string";
import {config} from "../config";
import {useNavigate} from "react-router-dom";
import {hasStoredAuthLoginErrorMessage} from "../utils/authLoginError";
import {getAuthSettings} from "../api/Auth0API";

const StyledWrapper = styled('div')(({theme}) => ({
  margin: theme.spacing(1),
  position: 'relative',
}));

const StyledCircularProgress = styled(({...props}) => (
  <CircularProgress size={24} {...props} />
))({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: -12,
  marginLeft: -12,
})

const Login = React.memo(() => {
  const { isAuthenticated, loading: authLoading, auth0Disabled, loginWithRedirect } = useAuth0()
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [emailSubmitting, setEmailSubmitting] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const queryString = QueryString.parse(location.search);
  const navigate = useNavigate();

  React.useEffect(()=> {
    if (hasStoredAuthLoginErrorMessage()) {
      return;
    }

    if(isAuthenticated) {
      navigate('/Home')
    } else if (!authLoading && auth0Disabled && !!sessionStorage.getItem("suiteManagerToken")) {
      redirectToSuiteManagerLogin();
    } else if (!authLoading && !auth0Disabled) {
      const redirectToLogin = async () => {
        await loginWithRedirect({
          appState: {targetUrl: config.redirectUri}
        });
      };
      redirectToLogin();
    }
  }, [auth0Disabled, authLoading, isAuthenticated, loginWithRedirect, navigate])

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailSubmitting(true);
    try {
      const settings = await getAuthSettings(email);
      if (settings?.workOsEnabled) {
        redirectToSuiteManagerLogin(email);
      } else if (settings?.workOsEnabled === false) {
        await loginWithRedirect({ login_hint: email });
      } else {
        // workOsEnabled is null/undefined — user not found in DashboardUser or Auth0
        setEmailError("We couldn't find your account. Please contact your management company for access.");
      }
    } catch {
      setEmailError("An error occurred. Please try again.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const loginWithSuiteManager = () => {
    let tokenExtracted = false;
    if (!!queryString.accessToken) {
      sessionStorage.setItem("suiteManagerToken", queryString.accessToken.toString());
      tokenExtracted = true;
    }
    if (!!queryString.refreshToken) {
      sessionStorage.setItem("suiteManagerRefresh", queryString.refreshToken.toString());
      tokenExtracted = true;
    }
    if (!!queryString.data) {
      try {
        const suiteManagerData = JSON.parse(window.atob(queryString.data.toString()));
        if (suiteManagerData?.accessToken) {
          sessionStorage.setItem("suiteManagerToken", suiteManagerData.accessToken.toString());
        }
        if (suiteManagerData?.refreshToken) {
          sessionStorage.setItem("suiteManagerRefresh", suiteManagerData.refreshToken.toString());
        }
        tokenExtracted = true;
      } catch (e) {
        console.error('Invalid Suite Manager data payload');
      }
    }
    if (tokenExtracted) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (!!sessionStorage.getItem("suiteManagerToken")) {
      navigate('/Home');
    } else {
      redirectToSuiteManagerLogin();
    }
  }

  return (
    <Grid container className="mt-5 pt-5">
      <Grid container item justifyContent="center" direction="column" alignItems="center" className="h-75">
        <img src={FrontStepsLogo} alt="FRONTSTEPS Payments" className="img-fluid max-w-100 m-4"/>
        <h3 className="font-size-lg font-weight-bold mt-3">FRONTSTEPS Payments</h3>
        {!isAuthenticated && auth0Disabled && (
          <StyledWrapper>
            <form onSubmit={handleEmailSubmit} style={{width: '300px'}}>
              <TextField
                type="email"
                label="Email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                required
                fullWidth
                variant="outlined"
                size="small"
                className="max-w-300 mb-2"
              />
              {emailError && (
                <Alert severity="error" className="max-w-300 mb-2">
                  {emailError}
                </Alert>
              )}
              <StyledWrapper>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  className="max-w-300"
                  disabled={emailSubmitting || !email}
                >
                  Continue
                </Button>
                {emailSubmitting && <StyledCircularProgress />}
              </StyledWrapper>
            </form>
          </StyledWrapper>
        )}
        {!isAuthenticated && !auth0Disabled && (
          <StyledWrapper>
            <StyledWrapper>
              <Button onClick={() => {setLoading(true); return loginWithRedirect({})}}
                      variant="contained"
                      color="primary"
                      fullWidth={true}
                      className="max-w-300"
                      disabled={loading}>
                Login with Auth0
              </Button>
              {loading && <StyledCircularProgress />}
            </StyledWrapper>
            <StyledWrapper>
              <Button onClick={() => {
                setLoading(true);
                return loginWithSuiteManager();
              }}
                      variant="contained"
                      color="inherit"
                      fullWidth={true}
                      className="max-w-300"
                      disabled={loading}>
                Login with Suite Manager
              </Button>
              {loading && <StyledCircularProgress />}
            </StyledWrapper>
          </StyledWrapper>
        )}
      </Grid>
    </Grid>
  )
});

export default Login;
